<template>
  <div class="min-h-screen relative overflow-hidden transition-colors duration-500 font-benny selection:bg-blue-500/30">
    <!-- Animierter Background - Konsistent mit Home & About -->
    <div class="fixed inset-0 -z-10">
      <!-- Gradient Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 dark:from-gray-900 dark:via-blue-950/20 dark:to-gray-800"></div>
      
      <!-- Animierte Gradient Blobs -->
      <div class="absolute top-0 -left-4 w-96 h-96 bg-blue-400/20 dark:bg-blue-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob"></div>
      <div class="absolute top-0 -right-4 w-96 h-96 bg-purple-400/20 dark:bg-purple-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      <div class="absolute -bottom-8 left-20 w-96 h-96 bg-pink-400/20 dark:bg-pink-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
      
      <!-- Grid Pattern Overlay -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)]"></div>
    </div>

    <Navigation />

    <main class="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-32 md:pt-40 pb-32 relative z-10">
      <!-- Header Section - Kompakter optimiert für maximale Sichtbarkeit -->
      <div class="grid lg:grid-cols-2 gap-6 items-end mb-8 relative animate-fade-in xl:-mx-8 2xl:-mx-16">
        <div class="text-left">
          <div 
            class="inline-flex items-center px-2.5 py-0.5 rounded-lg mb-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 no-select"
          >
            <p class="text-[9px] font-bold tracking-[0.2em] text-blue-600 dark:text-blue-400 uppercase">
              Portfolio
            </p>
          </div>
          
          <h1 class="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-3 tracking-tight animate-title">
            Meine <span class="text-blue-600">Projekte</span>
          </h1>
          
          <p class="text-sm md:text-base text-gray-600 dark:text-gray-400 max-w-xl leading-relaxed font-light">
            Individuelle Webanwendungen und spezialisierte WordPress-Lösungen – umgesetzt mit 
            <span class="text-gray-900 dark:text-white font-medium">Leidenschaft</span> und <span class="text-gray-900 dark:text-white font-medium">Präzision</span>.
          </p>
        </div>

        <div v-if="!loading" class="flex lg:justify-end items-center space-x-3 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
          <div class="flex items-center space-x-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm px-3 py-2 rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
            <span class="text-blue-600 dark:text-blue-400 text-base">{{ projects.length }}</span>
            <span>Projekte</span>
          </div>
          <div class="flex items-center space-x-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm px-3 py-2 rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
            <span class="text-purple-600 dark:text-purple-400 text-base">{{ availableTags.length }}</span>
            <span>Tech</span>
          </div>
        </div>
      </div>

      <!-- Filter Bar - Kompakter & Sticky -->
      <div class="mb-10 sticky top-24 z-30 transition-all duration-300 xl:-mx-8 2xl:-mx-16">
        <div class="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl p-1 rounded-2xl shadow-lg shadow-gray-200/30 dark:shadow-black/50 dark:shadow-2xl border border-gray-200/50 dark:border-gray-700/60">
          <ProjectFilterBar
            :search="searchQuery"
            @update:search="searchQuery = $event"
            :technologies="availableTags"
            :selected-tags="selectedTags"
            @toggle-tag="toggleTag"
            @reset="resetFilters"
          />
        </div>
      </div>

      <!-- Projects Grid mit Masonry-Style -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div v-for="i in 6" :key="i" class="h-[450px] glass-card animate-pulse rounded-[2rem]"></div>
      </div>

      <div v-else-if="filteredProjects.length === 0" class="max-w-xl mx-auto py-20">
        <EmptyState
          :icon="MagnifyingGlassIcon"
          title="Keine Treffer"
          message="Wir konnten keine Projekte finden, die deinen aktuellen Filtereinstellungen entsprechen."
          action-text="Filter komplett zurücksetzen"
          @action="resetFilters"
        />
      </div>

      <div 
        v-else 
        :class="gridClasses"
        class="xl:-mx-8 2xl:-mx-16"
      >
        <div 
          v-for="(project, index) in filteredProjects" 
          :key="project.id || project.title"
          class="animate-fade-in-up"
          :style="{ animationDelay: `${index * 100}ms` }"
        >
          <PortfolioItem
            :project="project"
            class="w-full h-full"
          />
        </div>
      </div>

      <!-- CTA Footer Section - Konsistent mit dem Site-Style -->
      <div v-if="!loading" class="mt-32 p-1 md:p-1.5 rounded-[3rem] bg-gradient-to-br from-blue-600/10 to-purple-600/10 dark:from-blue-500/5 dark:to-purple-500/5 backdrop-blur-3xl border border-gray-200/50 dark:border-white/5 relative overflow-hidden group transition-all duration-500 hover:from-blue-600/15 hover:to-purple-600/15 dark:hover:from-blue-500/10 dark:hover:to-purple-500/10 hover:shadow-2xl hover:shadow-blue-500/20 dark:hover:shadow-purple-500/20">
        <div class="bg-white/80 dark:bg-gray-900/80 rounded-[2.9rem] p-12 md:p-16 text-center relative z-10 overflow-hidden border border-white/50 dark:border-white/5 shadow-2xl shadow-gray-200/50 dark:shadow-black/20 transition-all duration-500 group-hover:bg-white/90 dark:group-hover:bg-gray-900/90 group-hover:shadow-2xl group-hover:shadow-blue-500/30 dark:group-hover:shadow-purple-500/30 group-hover:border-blue-200/50 dark:group-hover:border-blue-800/30">
          <!-- Animated Background Elements inside CTA -->
          <div class="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:bg-blue-500/15 group-hover:scale-110 transition-all duration-700 ease-out"></div>
          <div class="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl group-hover:bg-purple-500/15 group-hover:scale-110 transition-all duration-700 ease-out"></div>
          
          <h2 class="text-4xl md:text-5xl font-extrabold mb-6 text-gray-900 dark:text-white tracking-tight transition-all duration-500 group-hover:scale-[1.02]">
            Bereit für Ihr <span class="text-blue-600 dark:text-blue-400 transition-colors duration-500 group-hover:text-blue-700 dark:group-hover:text-blue-300">eigenes</span> Projekt?
          </h2>
          <p class="text-gray-600 dark:text-gray-400 mb-10 text-lg md:text-xl max-w-2xl mx-auto font-light leading-relaxed transition-all duration-500 group-hover:text-gray-700 dark:group-hover:text-gray-300">
            Gemeinsam realisieren wir Ihr Projekt. Von der Konzeption bis zur finalen Umsetzung begleite ich Sie professionell und zuverlässig.
          </p>
          <router-link
            to="/contact"
            class="group/btn relative inline-flex items-center justify-center px-10 py-4 bg-blue-600 dark:bg-blue-500 text-white font-bold rounded-2xl shadow-xl shadow-blue-500/30 transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-blue-500/50 active:scale-95 overflow-hidden"
          >
            <!-- Base Gradient Overlay - sanfter Fade-In -->
            <div class="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-600 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 ease-out"></div>
            
            <!-- Shimmer Effect -->
            <div class="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700 ease-in-out bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
            
            <span class="relative z-10 transition-all duration-300 group-hover/btn:translate-x-[-2px]">Kontakt aufnehmen</span>
            <svg class="relative z-10 w-5 h-5 ml-2 transition-all duration-300 group-hover/btn:translate-x-1 opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </router-link>
        </div>
      </div>
    </main>

    <StandardFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, computed } from 'vue'
import Navigation from '../components/Navigation.vue'
import StandardFooter from '../components/StandardFooter.vue'
import ProjectFilterBar from '../components/portfolio/ProjectFilterBar.vue'
import PortfolioItem from '../components/PortfolioItem.vue'
import EmptyState from '../components/EmptyState.vue'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { getPortfolioProjects, type PortfolioProject } from '../firebase/portfolio'
import { useProjectFilter } from '../composables/useProjectFilter'
import { useTheme } from '../composables/useTheme'
import { logger } from '../utils/logger'

const loading = ref(true)
const projects = ref<PortfolioProject[]>([])

// Stelle sicher, dass Theme initialisiert ist
const { initializeTheme } = useTheme()

const {
  searchQuery,
  selectedTags,
  availableTags,
  filteredProjects,
  toggleTag,
  resetFilters
} = useProjectFilter(projects)

// Dynamische Grid-Klassen basierend auf Projektanzahl
const gridClasses = computed(() => {
  const count = filteredProjects.value.length
  const baseClasses = 'grid grid-cols-1 md:grid-cols-2 gap-8'
  
  // Desktop (lg): Spezielle Anordnung für 4 und 5 Projekte
  // Auf großen Bildschirmen: Größere Gaps für bessere Textdarstellung
  if (count === 4) {
    return `${baseClasses} lg:grid-cols-2 lg:gap-8 xl:gap-10 2xl:gap-12` // 2x2 Grid
  } else if (count === 5) {
    return `${baseClasses} lg:grid-cols-3 lg:gap-8 xl:gap-10 2xl:gap-12` // 3 oben, 2 unten
  } else {
    return `${baseClasses} lg:grid-cols-3 lg:gap-8 xl:gap-10 2xl:gap-12` // Standard 3 Spalten
  }
})

onMounted(async () => {
  // Stelle sicher, dass Theme initialisiert ist, bevor Projekte geladen werden
  initializeTheme()
  await nextTick()
  
  try {
    const firebaseProjects = await getPortfolioProjects()
    const { combinePortfolioProjects } = await import('../utils/portfolioHelper')
    projects.value = await combinePortfolioProjects(firebaseProjects)
  } catch (error) {
    logger.error('Fehler beim Laden der Portfolio-Projekte', error)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
@keyframes pulse-slow {
  0%, 100% { opacity: 0.3; transform: scale(1) translate(0, 0); }
  50% { opacity: 0.5; transform: scale(1.1) translate(20px, -20px); }
}

.animate-pulse-slow {
  animation: pulse-slow 10s ease-in-out infinite;
}

@keyframes fade-in-up {
  from { 
    opacity: 0; 
    transform: translateY(40px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
}

.animate-fade-in-up {
  opacity: 0;
  animation: fade-in-up 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

/* Reduzierte Animationen für Nutzer mit prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .animate-fade-in-up {
    animation: none;
    opacity: 1;
    transform: none;
  }
}

@keyframes title-pop {
  0% { transform: scale(0.9); opacity: 0; filter: blur(10px); }
  100% { transform: scale(1); opacity: 1; filter: blur(0); }
}

.animate-title {
  animation: title-pop 1s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

.animate-fade-in {
  opacity: 0;
  animation: fade-in-up 1s ease-out forwards;
}

.animation-delay-500 { animation-delay: 500ms; }
.animation-delay-1000 { animation-delay: 1000ms; }
.animation-delay-2000 { animation-delay: 2s; }
.animation-delay-4000 { animation-delay: 4s; }

.font-benny {
  font-family: 'Neulis Sans', sans-serif;
}

/* Glassmorphism Styles (Backup falls Utilities nicht greifen) */
.glass-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.dark .glass-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.glass-card-heavy {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.dark .glass-card-heavy {
  background: rgba(17, 24, 39, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
</style>


